import { cbc } from "@noble/ciphers/aes.js";
import { bytesToUtf8, utf8ToBytes } from "@noble/ciphers/utils.js";
import { createTransform } from "redux-persist";

let aesKey = "";
let oldAesKey = "";

export function initialiseEncryption(config: {
  aesKey: string;
  oldAesKey?: string;
}): void {
  aesKey = config.aesKey ?? "";
  oldAesKey = config.oldAesKey ?? "";
}

// --- Base64 helpers ----------------------------------------------------------
// Tiny, dependency-free Base64 codec so the module works identically in
// React Native (Hermes), Expo Go, Expo Web, and Node without relying on
// `Buffer` polyfills or platform-specific atob/btoa.
const B64_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

function bytesToBase64(bytes: Uint8Array): string {
  let out = "";
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const triple = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
    out +=
      B64_ALPHABET[(triple >> 18) & 0x3f] +
      B64_ALPHABET[(triple >> 12) & 0x3f] +
      B64_ALPHABET[(triple >> 6) & 0x3f] +
      B64_ALPHABET[triple & 0x3f];
  }
  const remaining = bytes.length - i;
  if (remaining === 1) {
    const triple = bytes[i] << 16;
    out +=
      B64_ALPHABET[(triple >> 18) & 0x3f] +
      B64_ALPHABET[(triple >> 12) & 0x3f] +
      "==";
  } else if (remaining === 2) {
    const triple = (bytes[i] << 16) | (bytes[i + 1] << 8);
    out +=
      B64_ALPHABET[(triple >> 18) & 0x3f] +
      B64_ALPHABET[(triple >> 12) & 0x3f] +
      B64_ALPHABET[(triple >> 6) & 0x3f] +
      "=";
  }
  return out;
}

function base64ToBytes(b64: string): Uint8Array {
  let cleaned = b64.replace(/[^A-Za-z0-9+/=]/g, "");

  // Auto-pad base64 string if length is not a multiple of 4 (matches CryptoJS's lenient parsing)
  const mod = cleaned.length % 4;
  if (mod === 2) {
    cleaned += "==";
  } else if (mod === 3) {
    cleaned += "=";
  }

  if (cleaned.length === 0) return new Uint8Array(0);

  const padCount = cleaned.endsWith("==") ? 2 : cleaned.endsWith("=") ? 1 : 0;
  const byteLen = (cleaned.length / 4) * 3 - padCount;
  const out = new Uint8Array(byteLen);

  let p = 0;
  for (let i = 0; i < cleaned.length; i += 4) {
    const c0 = B64_ALPHABET.indexOf(cleaned[i]);
    const c1 = B64_ALPHABET.indexOf(cleaned[i + 1]);
    const c2 = cleaned[i + 2] === "=" ? 0 : B64_ALPHABET.indexOf(cleaned[i + 2]);
    const c3 = cleaned[i + 3] === "=" ? 0 : B64_ALPHABET.indexOf(cleaned[i + 3]);
    const triple = (c0 << 18) | (c1 << 12) | (c2 << 6) | c3;
    if (p < byteLen) out[p++] = (triple >> 16) & 0xff;
    if (p < byteLen) out[p++] = (triple >> 8) & 0xff;
    if (p < byteLen) out[p++] = triple & 0xff;
  }
  return out;
}

/**
 * Mirrors the backend's normalizeKeyBuffer exactly.
 *
 * The backend uses Buffer.alloc(targetSize) which zero-fills, then copies the
 * key bytes in. We replicate this by zero-padding (or truncating) the raw key
 * bytes to the nearest valid AES key size (16/24/32 bytes). The resulting byte
 * sequence is identical to what crypto-js used to produce, so existing
 * persisted Redux state and the backend wire format remain compatible.
 */
function normalizeKeyBytes(keyBytes: Uint8Array): Uint8Array {
  const validKeySizes = [16, 24, 32];
  const keyLen = keyBytes.length;

  if (validKeySizes.includes(keyLen)) {
    return keyBytes;
  }

  let targetSize: number;
  if (keyLen < 16) {
    targetSize = 16;
  } else if (keyLen < 24) {
    targetSize = 24;
  } else if (keyLen < 32) {
    targetSize = 32;
  } else {
    targetSize = 32; // truncate oversized keys
  }

  const padded = new Uint8Array(targetSize); // zero-filled
  padded.set(keyBytes.subarray(0, Math.min(keyLen, targetSize)));
  return padded;
}

function generateIV(): Uint8Array {
  // Prefer crypto.getRandomValues (polyfilled in native builds via
  // react-native-get-random-values, native on Web and Node). Fall back to a
  // time + Math.random mix for environments where the polyfill is unavailable
  // (e.g. Expo Go) — safe for an IV since an IV only needs to be unique, not
  // secret.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) {
      bytes[i] = (Date.now() ^ (Math.random() * 256) ^ (i * 37)) & 0xff;
    }
  }
  return bytes;
}

export function encryptString(data: string, oldString: boolean = false): string {
  try {
    const rawKey = base64ToBytes(oldString ? oldAesKey : aesKey);
    const keyBytes = normalizeKeyBytes(rawKey);
    const iv = generateIV();

    // @noble/ciphers `cbc` uses PKCS#7 padding by default — byte-identical to
    // the previous CryptoJS.AES.encrypt(..., { mode: CBC, padding: Pkcs7 })
    // output, so the backend continues to decrypt without change.
    const ciphertext = cbc(keyBytes, iv).encrypt(utf8ToBytes(data));

    // Wire format: "<iv_base64>:<ciphertext_base64>" — matches backend exactly.
    return bytesToBase64(iv) + ":" + bytesToBase64(ciphertext);
  } catch (error) {
    console.error("Encryption failed", error);
    throw new Error("Encryption failed");
  }
}

export function decryptString(encryptedData: string, oldString: boolean = false): string {
  try {
    const rawKey = base64ToBytes(oldString ? oldAesKey : aesKey);
    const keyBytes = normalizeKeyBytes(rawKey);

    const separatorIndex = encryptedData.indexOf(":");
    if (separatorIndex === -1) {
      throw new Error("Invalid encrypted data format — expected '<iv>:<ciphertext>'.");
    }

    const iv = base64ToBytes(encryptedData.slice(0, separatorIndex));
    const ciphertext = base64ToBytes(encryptedData.slice(separatorIndex + 1));

    const plaintext = cbc(keyBytes, iv).decrypt(ciphertext);
    return bytesToUtf8(plaintext);
  } catch (error) {
    throw new Error("Decryption failed: " + JSON.stringify(error));
  }
}

export const persistEncryptionTransform = createTransform(
  (inboundState) => {
    try {
      return encryptString(JSON.stringify(inboundState));
    } catch (e) {
      console.error("Encryption failed", e);
      return inboundState;
    }
  },
  (outboundState) => {
    try {
      if (typeof outboundState !== "string") return {};
      const decrypted = decryptString(outboundState);
      return JSON.parse(decrypted);
    } catch (e) {
      console.error("Decryption or JSON parse failed", e);
      return {};
    }
  },
);
